import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardHeader,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import DashboardLayout from "../../../../layoutcontainers/DashboardLayout";
import MaterialTypography from "../../../../components/MaterialTypography";
import MaterialDataGrid from "../../../../components/MaterialDataGrid";
import MaterialBox from "../../../../components/MaterialBox";
import MaterialSelect from "../../../../components/MasterSelect";
import Swal from "sweetalert2";
import { useLoading } from "../../../../context/LodingContext";
import { downloadExcelFile } from "../../../function/downloadExcel";
import URL from "../../../../pages/Utils/url";
import request from "graphql-request";
import { gql } from "@apollo/client";
import BASEURL from "../../../../pages/Utils/Baseurl";
import { allUsers } from "../../employee/employeeData";
import ReusableFilter from "../../../components/ReusableFilter";
import AppliedFilter from "../AppliedFilter/AppliedFilter";
import ReportGraphPanel from "../ReportGraphPanel";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const employeeCheckinCheckoutReport = gql`
  query EmployeeCheckinCheckoutReport(
    $startDate: Date!
    $endDate: Date!
    $employeeId: Int
    $search: String
    $page: Int
    $pageSize: Int
  ) {
    employeeCheckinCheckoutReport(
      startDate: $startDate
      endDate: $endDate
      employeeId: $employeeId
      search: $search
      page: $page
      pageSize: $pageSize
    ) {
      success
      message
      totalCount
      currentPage
      totalPages
      hasNextPage
      hasPreviousPage
      data {
        id
        date
        employeeCode
        employeeName
        role
        department
        checkIn
        checkOut
        duration
      }
    }
  }
`;

const exportEmployeeCheckInOutReport = gql`
  mutation ExportEmployeeCheckInOutReport(
    $startDate: Date!
    $endDate: Date!
    $employeeId: Int
    $employeeIds: [Int]
    $search: String
  ) {
    exportEmployeeCheckinCheckoutReport(
      startDate: $startDate
      endDate: $endDate
      employeeId: $employeeId
      employeeIds: $employeeIds
      search: $search
    ) {
      success
      error
      message
      fileUrl
    }
  }
`;

const initialFilters = {
  fromDate: "",
  toDate: "",
  employee: null,
  search: "",
};
const initialPaginationModel = { page: 0, pageSize: 10 };

const columns = [
  { field: "date", headerName: "Date", flex: 1, minWidth: 115 },
  { field: "employeeCode", headerName: "Employee Code", flex: 1, minWidth: 130 },
  { field: "employeeName", headerName: "Employee", flex: 1.2, minWidth: 160 },
  { field: "role", headerName: "Role", flex: 1, minWidth: 115 },
  { field: "department", headerName: "Department", flex: 1, minWidth: 130 },
  { field: "checkIn", headerName: "Check In", flex: 1, minWidth: 105 },
  { field: "checkOut", headerName: "Check Out", flex: 1, minWidth: 105 },
  { field: "duration", headerName: "Duration", flex: 1, minWidth: 105 },
];

function CheckinCheckout() {
  const { setCirleLoading } = useLoading();
  const [filterValues, setFilterValues] = useState(initialFilters);
  const filterValuesRef = useRef(initialFilters);
  const [activeFilters, setActiveFilters] = useState(initialFilters);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState("");
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [employeeError, setEmployeeError] = useState("");
  const [rows, setRows] = useState([]);
  const [value, setValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isTable, setIsTable] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [paginationModel, setPaginationModel] = useState(initialPaginationModel);
  const [reportError, setReportError] = useState("");

  const employeeOptions = employees.map((employee) => ({
    value: employee.id,
    label: employee.employeeCode
      ? `${employee.employeeName} (${employee.employeeCode})`
      : employee.employeeName,
  }));

  const graphData = rows.reduce((dailyAttendance, row) => {
    const date = row.date || "Unknown";
    const dailyRecord = dailyAttendance.find((entry) => entry.date === date);

    if (dailyRecord) {
      dailyRecord.checkIns += row.checkIn ? 1 : 0;
      dailyRecord.checkOuts += row.checkOut ? 1 : 0;
    } else {
      dailyAttendance.push({
        date,
        checkIns: row.checkIn ? 1 : 0,
        checkOuts: row.checkOut ? 1 : 0,
      });
    }

    return dailyAttendance;
  }, []);

  const fetchReport = useCallback(async (filters, model) => {
    setIsLoading(true);
    setIsTable(true);
    setReportError("");

    try {
      const result = await request(BASEURL, employeeCheckinCheckoutReport, {
        startDate: filters.fromDate,
        endDate: filters.toDate,
        employeeId: filters.employee?.value
          ? Number(filters.employee.value)
          : null,
        search: filters.search.trim(),
        page: model.page + 1,
        pageSize: model.pageSize,
      });
      const report = result?.employeeCheckinCheckoutReport;
      const currentPage = Math.max((report?.currentPage || model.page + 1) - 1, 0);

      setRows(report?.data || []);
      setTotalCount(report?.totalCount || 0);
      setTotalPages(report?.totalPages || 0);
      setPaginationModel({ page: currentPage, pageSize: model.pageSize });
    } catch (error) {
      setRows([]);
      setTotalCount(0);
      setTotalPages(0);
      setReportError(
        error?.response?.errors?.[0]?.message ||
        error?.message ||
        "Unable to load employee check-in/check-out records.",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    request(BASEURL, allUsers)
      .then((result) => setEmployees(result?.allUsers || []))
      .catch((error) => {
        setEmployeeError(error?.message || "Unable to load employees.");
        setEmployees([]);
      })
      .finally(() => setLoadingEmployees(false));
  }, []);

  const dispatchFilter = (action) => {
    let updatedFilters;

    if (action.name === "removeFilterValue") {
      updatedFilters = {
        ...filterValuesRef.current,
        [action.field]: null,
      };
    } else {
      updatedFilters = {
        ...filterValuesRef.current,
        [action.name]: action.value,
      };
    }

    filterValuesRef.current = updatedFilters;
    setFilterValues(updatedFilters);
  };

  const applyFilters = () => {
    const filters = { ...filterValuesRef.current };
    const firstPage = { page: 0, pageSize: paginationModel.pageSize };

    setActiveFilters(filters);
    setPaginationModel(firstPage);
    fetchReport(filters, firstPage);
    setDialogOpen(false);
  };

  const applySearch = (event) => {
    event.preventDefault();
    const filters = {
      ...activeFilters,
      search: searchDraft.trim(),
    };
    const firstPage = { page: 0, pageSize: paginationModel.pageSize };

    filterValuesRef.current = {
      ...filterValuesRef.current,
      search: filters.search,
    };
    setFilterValues(filterValuesRef.current);
    setActiveFilters(filters);
    setPaginationModel(firstPage);
    fetchReport(filters, firstPage);
  };

  const resetFilters = () => {
    const filters = { ...initialFilters };
    const firstPage = { page: 0, pageSize: initialPaginationModel.pageSize };

    filterValuesRef.current = filters;
    setFilterValues(filters);
    setActiveFilters(filters);
    setPaginationModel(firstPage);
    setSearchDraft("");
    setRows([]);
    setTotalCount(0);
    setTotalPages(0);
    setReportError("");
    setIsTable(false);
    setValue(0);
  };

  const handlePaginationChange = (model) => {
    if (
      model.page === paginationModel.page &&
      model.pageSize === paginationModel.pageSize
    ) {
      return;
    }

    const nextModel = {
      page: model.pageSize !== paginationModel.pageSize ? 0 : model.page,
      pageSize: model.pageSize,
    };
    setPaginationModel(nextModel);
    fetchReport(activeFilters, nextModel);
  };

  const handleGraphPageChange = (page) => {
    if (isLoading || page < 0 || page >= totalPages) return;
    const nextModel = { ...paginationModel, page };
    setPaginationModel(nextModel);
    fetchReport(activeFilters, nextModel);
  };

  const handleTabChange = (event, newValue) => setValue(newValue);

  const handleDownload = () => {
    const fromDate = activeFilters?.fromDate || filterValues?.fromDate;
    const toDate = activeFilters?.toDate || filterValues?.toDate;
    const employee = activeFilters?.employee || filterValues?.employee;
    const search = (
      activeFilters?.search ??
      filterValues?.search ??
      searchDraft ??
      ""
    ).trim();

    if (!fromDate || !toDate) {
      Swal.fire({
        position: "top",
        icon: "warning",
        title: "Please select start and end date to export.",
        showConfirmButton: true,
      });
      return;
    }

    setCirleLoading(true);
    request(BASEURL, exportEmployeeCheckInOutReport, {
      startDate: fromDate,
      endDate: toDate,
      employeeId: employee?.value ? Number(employee.value) : null,
      search: search || "",
    })
      .then(async (res) => {
        const report = res?.exportEmployeeCheckinCheckoutReport;
        const status = report?.success;

        if (status && report?.fileUrl) {
          const fileUrl = report.fileUrl;
          const pdfurl = `${URL}${fileUrl.startsWith("/") ? fileUrl.slice(1) : fileUrl}`;
          await downloadExcelFile(
            pdfurl,
            "employee_checkin_checkout_report.xlsx",
            report?.message || "Excel downloaded successfully.",
          );
        } else {
          Swal.fire({
            position: "top",
            icon: "error",
            title: report?.message || "Failed to export report.",
            showConfirmButton: true,
          });
        }
      })
      .catch((err) => {
        const message =
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          "Failed to export file.";
        Swal.fire({
          position: "top",
          icon: "error",
          title: message,
          showConfirmButton: true,
        });
      })
      .finally(() => {
        setCirleLoading(false);
      });
  };

  return (
    <DashboardLayout>
      <Card>
        <CardHeader
          title={
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Stack direction="column" spacing={0}>
                <MaterialTypography variant="h5" fontWeight="medium">
                  Employee Check-in / Check-out Report
                </MaterialTypography>
              </Stack>
              <Box sx={{ mx: "5%" }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  {rows.length > 0 && (
                    <Box sx={{ width: "100%", bgcolor: "background.paper" }}>
                      <Tabs
                        value={value}
                        onChange={handleTabChange}
                        TabIndicatorProps={{
                          style: { backgroundColor: "#2a9941" },
                        }}
                        sx={{ px: 2 }}
                        centered
                      >
                        <Tab sx={{ px: 2 }} label="Table list view" />
                        <Tab sx={{ px: 2 }} label="Graph view" />
                      </Tabs>
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      bgcolor: "background.paper",
                    }}
                  >
                    <IconButton
                      onClick={() => setDialogOpen(true)}
                      aria-label="Filters"
                      color="primary"
                    >
                      <FilterListIcon fontSize="medium" />
                    </IconButton>
                    <IconButton
                      onClick={resetFilters}
                      aria-label="Reset filters"
                      color="primary"
                    >
                      <RefreshIcon fontSize="medium" />
                    </IconButton>
                  </Box>
                </Stack>
              </Box>

              {/* Export File button, visible only when dates are selected */}
              {((activeFilters?.fromDate && activeFilters?.toDate) ||
                (filterValues?.fromDate && filterValues?.toDate)) && (
                <button
                  className="container-btn-file"
                  onClick={handleDownload}
                  style={{
                    height: "38px",
                    marginLeft: "0",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  <svg
                    fill="#fff"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 50 50"
                  >
                    <path d="M28.8125 .03125L.8125 5.34375C.339844 5.433594 0 5.863281 0 6.34375L0 43.65625C0 44.136719 .339844 44.566406 .8125 44.65625L28.8125 49.96875C28.875 49.980469 28.9375 50 29 50C29.230469 50 29.445313 49.929688 29.625 49.78125C29.855469 49.589844 30 49.296875 30 49L30 1C30 .703125 29.855469 .410156 29.625 .21875C29.394531 .0273438 29.105469 -.0234375 28.8125 .03125ZM32 6L32 13L34 13L34 15L32 15L32 20L34 20L34 22L32 22L32 27L34 27L34 29L32 29L32 35L34 35L34 37L32 37L32 44L47 44C48.101563 44 49 43.101563 49 42L49 8C49 6.898438 48.101563 6 47 6ZM36 13L44 13L44 15L36 15ZM6.6875 15.6875L11.8125 15.6875L14.5 21.28125C14.710938 21.722656 14.898438 22.265625 15.0625 22.875L15.09375 22.875C15.199219 22.511719 15.402344 21.941406 15.6875 21.21875L18.65625 15.6875L23.34375 15.6875L17.75 24.9375L23.5 34.375L18.53125 34.375L15.28125 28.28125C15.160156 28.054688 15.035156 27.636719 14.90625 27.03125L14.875 27.03125C14.8125 27.316406 14.664063 27.761719 14.4375 28.34375L11.1875 34.375L6.1875 34.375L12.15625 25.03125ZM36 20L44 20L44 22L36 22ZM36 27L44 27L44 29L36 29ZM36 35L44 35L44 37L36 37Z" />
                  </svg>
                  Export File
                </button>
              )}
            </Stack>
          }
        />

        <AppliedFilter
          onSave={applyFilters}
          data={filterValues}
          dispatch={dispatchFilter}
          chipDisplay={["employee"]}
        />

        <ReusableFilter
          data={filterValues}
          dispatch={dispatchFilter}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSave={applyFilters}
          title="Employee Check-in / Check-out Filter"
        >
          <Grid size={{ xs: 12, sm: 12 }}>
            <MaterialBox lineHeight={0} display="inline-block">
              <MaterialTypography
                component="label"
                variant="caption"
                fontWeight="bold"
              >
                Employee
              </MaterialTypography>
            </MaterialBox>
            <MaterialSelect
              name="employee"
              value={filterValues.employee}
              options={employeeOptions}
              onChange={(option) =>
                dispatchFilter({ name: "employee", value: option })
              }
              isLoading={loadingEmployees}
              isClearable
              placeholder="All employees"
            />
          </Grid>
        </ReusableFilter>

        {(employeeError || reportError) && (
          <Alert severity="error" sx={{ mx: 2.5, mb: 2 }}>
            {employeeError || reportError}
          </Alert>
        )}

        {!isLoading && isTable && (
          <>
            {value === 0 ? (
              <>
                <Box
                  component="form"
                  onSubmit={applySearch}
                  sx={{ width: "16rem", ml: "auto", p: 1 }}
                >
                  <TextField
                    fullWidth
                    autoFocus
                    variant="outlined"
                    size="small"
                    placeholder="Search..."
                    value={searchDraft}
                    onChange={(event) => setSearchDraft(event.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <MaterialDataGrid
                  rows={rows}
                  columns={columns}
                  isSelectable={false}
                  loading={isLoading}
                  hideSearch
                  paginationMode="server"
                  paginationModel={paginationModel}
                  onPaginationModelChange={handlePaginationChange}
                  rowCount={totalCount}
                  localeText={{
                    noRowsLabel: "No attendance records for these filters.",
                  }}
                />
              </>
            ) : (
              <ReportGraphPanel
                eyebrow="EMPLOYEE ATTENDANCE"
                title="Check-ins and check-outs"
                description="Daily attendance events for the selected report page"
                page={paginationModel.page}
                pageCount={totalPages}
                pageSize={paginationModel.pageSize}
                totalCount={totalCount}
                isLoading={isLoading}
                onPageChange={handleGraphPageChange}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={graphData}
                    margin={{ top: 24, right: 22, left: 4, bottom: 48 }}
                    barSize={22}
                  >
                    <CartesianGrid stroke="#e4ebe5" strokeDasharray="3 5" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: "#637168" }}
                      tickMargin={8}
                      interval={0}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#637168" }} />
                    <Tooltip />
                    <Legend
                      verticalAlign="top"
                      align="right"
                      height={24}
                      wrapperStyle={{ fontSize: "11px", paddingBottom: "6px" }}
                    />
                    <Bar dataKey="checkIns" fill="#287b45" name="Check-ins" />
                    <Bar dataKey="checkOuts" fill="#e4a64a" name="Check-outs" />
                  </BarChart>
                </ResponsiveContainer>
              </ReportGraphPanel>
            )}
          </>
        )}
      </Card>
    </DashboardLayout>
  );
}

export default CheckinCheckout;
